/**
 * Semantic non-vacuity for content escrow.
 *
 * A structurally complete archive is not necessarily evidence that user
 * content was captured. Schema metadata, corpus witnesses, fences, cached
 * policy pins, and settled/dangling intent rows can all exist in a wrong or
 * already-cleared storage partition. Only the content families below make an
 * archive non-vacuous.
 *
 * This module is shared by the in-app exporter and verifier and is bundled
 * unchanged into the standalone snippet. scripts/decrypt-content-escrow.mjs
 * carries the deliberate zero-dependency mirror; change both in lockstep.
 */

import { canonicalStringify, sha256Hex } from './canonical';
import type { EscrowManifest, EscrowRow } from './types';

/** Stores whose rows are themselves recoverable content, even if malformed. */
export const SEMANTIC_CONTENT_STORES: readonly string[] = [
  'prayers',
  'audio-queue',
  'bible-bookmarks',
  'bible-highlights',
  'content-entities',
  'content-holding',
  'content-pending',
  'content-staged-conflicts',
  'content-conflicts',
];

const RECOVERY_TEXT_KEYS = new Set([
  'adia-prayer-draft',
  'adia-interrupted-interim',
]);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SHA256_RE = /^[0-9a-f]{64}$/;
const DECIMAL_RE = /^(0|[1-9][0-9]*)$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const wanted = [...expected].sort();
  return JSON.stringify(actual) === JSON.stringify(wanted);
}

/**
 * A draft is raw text. Current interrupted-interim values are JSON objects,
 * while early builds/probes may carry the raw interim string. Preserve both
 * real content forms, but do not let whitespace or empty bookkeeping JSON
 * make an archive non-vacuous.
 */
export function hasNonEmptyRecoveryText(entry: unknown): boolean {
  if (!isRecord(entry) || !RECOVERY_TEXT_KEYS.has(String(entry.key))) return false;
  if (typeof entry.key !== 'string' || typeof entry.value !== 'string') return false;
  if (entry.key === 'adia-prayer-draft') return entry.value.trim().length > 0;
  if (entry.value.trim().length === 0) return false;
  try {
    const parsed = JSON.parse(entry.value) as unknown;
    if (typeof parsed === 'string') return parsed.trim().length > 0;
    if (!isRecord(parsed)) return false;
    return typeof parsed.text === 'string' && parsed.text.trim().length > 0;
  } catch {
    // Historical/raw browser interim text is still user-authored content.
    return true;
  }
}

/**
 * True only for an exact, unresolved LocalKeepsakeValidityIntentV1 whose
 * authenticated mutation digest matches its protection payload. Fences,
 * epochs, pins, settled intents, malformed rows, and dangling journal rows do
 * not count merely because the v9 stores contain something.
 */
export async function isValidUnresolvedProtectionIntent(row: EscrowRow): Promise<boolean> {
  const value = row.value;
  if (!isRecord(value)) return false;
  const keys = [
    'expectedDeletion',
    'expectedPolicyRevision',
    'expectedVisibility',
    'localSeq',
    'mutationDigest',
    'mutationId',
    'mutationKind',
    'ownerScope',
    'prayerId',
    'receiptOwnerRevision',
    'receiptPolicyRevision',
    'state',
  ];
  if (!hasExactKeys(value, keys)) return false;
  if (
    typeof row.key !== 'string' ||
    typeof value.mutationId !== 'string' ||
    row.key !== value.mutationId ||
    !UUID_RE.test(value.mutationId) ||
    typeof value.ownerScope !== 'string' ||
    !UUID_RE.test(value.ownerScope) ||
    typeof value.prayerId !== 'string' ||
    !UUID_RE.test(value.prayerId) ||
    !['set-private', 'set-included', 'delete', 'restore'].includes(
      String(value.mutationKind),
    ) ||
    typeof value.expectedPolicyRevision !== 'string' ||
    !DECIMAL_RE.test(value.expectedPolicyRevision) ||
    !['included', 'private'].includes(String(value.expectedVisibility)) ||
    !['live', 'deleted'].includes(String(value.expectedDeletion)) ||
    typeof value.localSeq !== 'string' ||
    !DECIMAL_RE.test(value.localSeq) ||
    !['local-only', 'in-flight'].includes(String(value.state)) ||
    value.receiptPolicyRevision !== null ||
    value.receiptOwnerRevision !== null ||
    typeof value.mutationDigest !== 'string' ||
    !SHA256_RE.test(value.mutationDigest)
  ) {
    return false;
  }

  const payload = canonicalStringify({
    expectedDeletion: value.expectedDeletion,
    expectedOwner: value.ownerScope,
    expectedPolicyRevision: value.expectedPolicyRevision,
    expectedVisibility: value.expectedVisibility,
    mutationId: value.mutationId,
    mutationKind: value.mutationKind,
    mutationVersion: 1,
    prayerId: value.prayerId,
  });
  return (await sha256Hex(payload)) === value.mutationDigest;
}

/** Semantic, not row-count-only, answer to "did this context hold content?" */
export async function hasSemanticRecoveryContent(manifest: EscrowManifest): Promise<boolean> {
  const coreStores = new Set(SEMANTIC_CONTENT_STORES);
  if (manifest.stores.some(
    (store) => isRecord(store) &&
      typeof store.name === 'string' &&
      coreStores.has(store.name) &&
      typeof store.rowCount === 'number' &&
      store.rowCount > 0,
  )) {
    return true;
  }
  if (manifest.localStorage.entries.some(hasNonEmptyRecoveryText)) return true;
  // Preserve even malformed/future attempt rows as recovery content; readers
  // fail closed on their schema rather than mistaking a draft-only profile for empty.
  const appMeta = manifest.stores.find(store => store.name === 'app-meta');
  if (appMeta && appMeta.summaryOnly !== true && Array.isArray(appMeta.rows) && appMeta.rows.some(row => typeof row.key === 'string' && row.key.startsWith('prayer-authoring-attempt-v1:'))) return true;

  const intents = manifest.stores.find(
    (store) => isRecord(store) &&
      store.name === 'keepsake-intents' &&
      store.summaryOnly !== true,
  );
  if (!intents || intents.summaryOnly === true || !Array.isArray(intents.rows)) return false;
  for (const row of intents.rows) {
    if (await isValidUnresolvedProtectionIntent(row)) return true;
  }
  return false;
}
